import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import {
	mediaQueryLarge,
	isMobileBreakpoint,
	getIOSVersion,
} from "@theme/utilities";

export class QuickAddComponent extends Component {
	/** @type {AbortController | null} */
	#abortController = null;
	/** @type {Map<string, Element>} */
	#cachedContent = new Map();

	get productPageUrl() {
		const productCard =
			/** @type {import('./product-card').ProductCard | null} */ (
				this.closest("product-card")
			);
		const productLink = productCard?.getProductCardLink();

		if (!productLink?.href) return "";

		const url = new URL(productLink.href);

		if (url.searchParams.has("variant")) {
			return url.toString();
		}

		const selectedVariantId = this.#getSelectedVariantId();
		if (selectedVariantId) {
			url.searchParams.set("variant", selectedVariantId);
		}

		return url.toString();
	}

	/**
	 * Gets the currently selected variant ID from the product card
	 * @returns {string | null} The variant ID or null
	 */
	#getSelectedVariantId() {
		const productCard =
			/** @type {import('./product-card').ProductCard | null} */ (
				this.closest("product-card")
			);
		return productCard?.getSelectedVariantId() || null;
	}

	connectedCallback() {
		super.connectedCallback();

		mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
		this.#abortController?.abort();
	}

	/**
	 * Handles quick add button click
	 * @param {Event} event - The click event
	 */
	handleClick = async event => {
		event.preventDefault();

		const currentUrl = this.productPageUrl;

		// Check if we have cached content for this URL
		let productGrid = this.#cachedContent.get(currentUrl);

		if (!productGrid) {
			// Fetch and cache the content
			const html = await this.fetchProductPage(currentUrl);
			if (html) {
				const gridElement = html.querySelector(
					"[data-product-grid-content]",
				);
				if (gridElement) {
					// Cache the cloned element to avoid modifying the original
					productGrid = /** @type {Element} */ (
						gridElement.cloneNode(true)
					);
					this.#cachedContent.set(currentUrl, productGrid);
				}
			}
		}

		if (productGrid) {
			// Use a fresh clone from the cache
			const freshContent = /** @type {Element} */ (
				productGrid.cloneNode(true)
			);
			await this.updateQuickAddModal(freshContent);
		}

		this.#openQuickAddModal();
	};

	/** @param {QuickAddDialog} dialogComponent */
	#stayVisibleUntilDialogCloses(dialogComponent) {
		this.toggleAttribute("stay-visible", true);

		dialogComponent.addEventListener(
			DialogCloseEvent.eventName,
			() => this.toggleAttribute("stay-visible", false),
			{
				once: true,
			},
		);
	}

	#openQuickAddModal = () => {
		const dialogComponent = document.getElementById("quick-add-dialog");
		if (!(dialogComponent instanceof QuickAddDialog)) return;

		this.#stayVisibleUntilDialogCloses(dialogComponent);

		dialogComponent.showDialog();
	};

	#closeQuickAddModal = () => {
		const dialogComponent = document.getElementById("quick-add-dialog");
		if (!(dialogComponent instanceof QuickAddDialog)) return;

		dialogComponent.closeDialog();
	};

	/**
	 * Fetches the product page content
	 * @param {string} productPageUrl - The URL of the product page to fetch
	 * @returns {Promise<Document | null>}
	 */
	async fetchProductPage(productPageUrl) {
		if (!productPageUrl) return null;

		// We use this to abort the previous fetch request if it's still pending.
		this.#abortController?.abort();
		this.#abortController = new AbortController();

		try {
			const response = await fetch(productPageUrl, {
				signal: this.#abortController.signal,
			});

			if (!response.ok) {
				throw new Error(
					`Failed to fetch product page: HTTP error ${response.status}`,
				);
			}

			const responseText = await response.text();
			const html = new DOMParser().parseFromString(
				responseText,
				"text/html",
			);

			return html;
		} catch (error) {
			if (error.name === "AbortError") {
				return null;
			} else {
				throw error;
			}
		} finally {
			this.#abortController = null;
		}
	}

	/**
	 * Re-renders the variant picker.
	 * @param {Element} productGrid - The product grid element
	 */
	async updateQuickAddModal(productGrid) {
		const modalContent = document.getElementById("quick-add-modal-content");

		if (!productGrid || !modalContent) return;

		if (isMobileBreakpoint()) {
			const productDetails = productGrid.querySelector(".product-details");
			const productFormComponent = productGrid.querySelector(
				"product-form-component",
			);
			const variantPicker = productGrid.querySelector("variant-picker");
			const productPrice = productGrid.querySelector("product-price");
			const productTitle = document.createElement("a");
			productTitle.textContent = this.dataset.productTitle || "";

			// Make product title as a link to the product page
			productTitle.href = this.productPageUrl;

			const productHeader = document.createElement("div");
			productHeader.classList.add("product-header");

			productHeader.appendChild(productTitle);
			if (productPrice) {
				productHeader.appendChild(productPrice);
			}
			productGrid.appendChild(productHeader);

			if (variantPicker) {
				productGrid.appendChild(variantPicker);
			}
			if (productFormComponent) {
				productGrid.appendChild(productFormComponent);
			}

			productDetails?.remove();
		}

		morph(modalContent, productGrid);

		this.#syncVariantSelection(modalContent);
		this.#initSubscriptionWidget(modalContent);

		const modalAddBtn = modalContent.querySelector(
			".add-to-cart-text__content",
		);
		// Transform text to lowercase and check instead
		const modalAddBtnContent = modalAddBtn?.textContent?.toLowerCase();
		if (modalAddBtnContent?.includes("add")) {
			modalAddBtn.textContent = "Add";
		}
	}

	/**
	 * Syncs the variant selection from the product card to the modal
	 * @param {Element} modalContent - The modal content element
	 */
	#syncVariantSelection(modalContent) {
		const selectedVariantId = this.#getSelectedVariantId();
		if (!selectedVariantId) return;

		// Find and check the corresponding input in the modal
		const modalInputs = modalContent.querySelectorAll(
			'input[type="radio"][data-variant-id]',
		);
		for (const input of modalInputs) {
			if (
				input instanceof HTMLInputElement &&
				input.dataset.variantId === selectedVariantId &&
				!input.checked
			) {
				input.checked = true;
				input.dispatchEvent(new Event("change", { bubbles: true }));
				break;
			}
		}
	}

	/**
	 * Initializes the subscription widget functionality in the modal
	 * @param {Element} modalContent - The modal content element
	 */
	#initSubscriptionWidget(modalContent) {
		const subscriptionContainer = /** @type {HTMLElement | null} */ (
			modalContent.querySelector("[data-subscription-widget]")
		);
		if (!subscriptionContainer) return;

		// Parse selling plans data
		/** @type {Array<{id: string, name: string, frequencyText: string, isFirst: boolean}>} */
		let sellingPlans = [];
		try {
			sellingPlans = JSON.parse(
				subscriptionContainer.dataset.sellingPlans || "[]",
			);
		} catch (e) {
			console.error("Failed to parse selling plans data:", e);
			return;
		}

		if (sellingPlans.length === 0) return;

		// Get DOM elements
		const checkbox = /** @type {HTMLInputElement | null} */ (
			subscriptionContainer.querySelector("[data-subscription-checkbox]")
		);
		const frequencyWrapper = /** @type {HTMLElement | null} */ (
			subscriptionContainer.querySelector(
				"[data-subscription-frequency-wrapper]",
			)
		);
		const hiddenSelect = /** @type {HTMLSelectElement | null} */ (
			subscriptionContainer.querySelector("[data-selling-plan-select]")
		);
		const dropdownButton = subscriptionContainer.querySelector(
			"[data-subscription-dropdown-button]",
		);
		const dropdownList = subscriptionContainer.querySelector(
			"[data-subscription-dropdown-list]",
		);
		const selectedText = subscriptionContainer.querySelector(
			"[data-subscription-selected-text]",
		);

		if (!checkbox || !frequencyWrapper || !hiddenSelect) return;

		// Set default plan
		let currentPlanId = sellingPlans[0]?.id || null;

		/**
		 * Updates the dropdown button text
		 * @param {string} planId - The plan ID
		 */
		const updateButtonText = planId => {
			if (!selectedText) return;
			const plan = sellingPlans.find(p => p.id === planId);
			if (!plan) return;

			selectedText.innerHTML = "";
			selectedText.appendChild(
				document.createTextNode("Ships Every: " + plan.frequencyText),
			);

			if (plan.isFirst) {
				const recommendedSpan = document.createElement("span");
				recommendedSpan.className = "subscription-recommended";
				recommendedSpan.textContent = " - Recommended";
				selectedText.appendChild(recommendedSpan);
			}
		};

		/**
		 * Updates the selected option styling
		 * @param {string} planId - The plan ID
		 */
		const updateSelectedOption = planId => {
			if (!dropdownList) return;
			const options = dropdownList.querySelectorAll(
				".subscription-frequency-option",
			);
			options.forEach(opt => {
				const optElement = /** @type {HTMLElement} */ (opt);
				const isSelected = optElement.dataset.sellingPlanId === planId;
				opt.classList.toggle(
					"subscription-frequency-option--selected",
					isSelected,
				);
				opt.setAttribute("aria-selected", isSelected ? "true" : "false");
			});
		};

		/**
		 * Selects a selling plan
		 * @param {string} planId - The plan ID
		 */
		const selectPlan = planId => {
			currentPlanId = planId;
			if (hiddenSelect) {
				hiddenSelect.value = planId;
			}
			updateSelectedOption(planId);
			updateButtonText(planId);
		};

		/**
		 * Gets the modal content element
		 * @returns {HTMLElement | null}
		 */
		const getModalContent = () => {
			return /** @type {HTMLElement | null} */ (
				document.getElementById("quick-add-modal-content")
			);
		};

		/**
		 * Opens the dropdown
		 */
		const openDropdown = () => {
			dropdownList?.classList.add("subscription-frequency-list--open");
			dropdownButton?.setAttribute("aria-expanded", "true");

			// Dynamically add padding to modal content based on dropdown height
			requestAnimationFrame(() => {
				const dropdownHeight =
					dropdownList?.getBoundingClientRect().height || 0;
				const modalContent = getModalContent();
				if (modalContent && dropdownHeight > 0) {
					modalContent.style.paddingBottom = `${dropdownHeight + 30}px`;
				}
			});
		};

		/**
		 * Closes the dropdown
		 */
		const closeDropdown = () => {
			dropdownList?.classList.remove("subscription-frequency-list--open");
			dropdownButton?.setAttribute("aria-expanded", "false");

			// Remove the dynamic padding
			const modalContent = getModalContent();
			if (modalContent) {
				modalContent.style.paddingBottom = "";
			}
		};

		/**
		 * Toggles variant prices between regular and subscription
		 * @param {boolean} isSubscription - Whether subscription is active
		 */
		const toggleVariantPrices = (isSubscription) => {
			const scope = subscriptionContainer.closest('#quick-add-modal-content') || subscriptionContainer.closest('form');
			if (!scope) return;
			scope.querySelectorAll('[data-variant-regular-price]').forEach(el => {
				/** @type {HTMLElement} */ (el).style.display = isSubscription ? 'none' : '';
			});
			scope.querySelectorAll('[data-variant-subscription-price]').forEach(el => {
				/** @type {HTMLElement} */ (el).style.display = isSubscription ? '' : 'none';
			});
			scope.querySelectorAll('[data-variant-compare-price]').forEach(el => {
				/** @type {HTMLElement} */ (el).style.display = isSubscription ? '' : 'none';
			});
		};

		/**
		 * Enables subscription
		 */
		const enableSubscription = () => {
			frequencyWrapper.style.removeProperty("display");
			frequencyWrapper.style.display = "block";
			if (hiddenSelect) {
				hiddenSelect.disabled = false;
				hiddenSelect.value = currentPlanId || "";
			}
			toggleVariantPrices(true);
		};

		/**
		 * Disables subscription
		 */
		const disableSubscription = () => {
			frequencyWrapper.style.display = "none";
			if (hiddenSelect) {
				hiddenSelect.disabled = true;
			}
			closeDropdown();
			toggleVariantPrices(false);
		};

		// Initialize default state (unchecked)
		checkbox.checked = false;
		disableSubscription();
		const initialPlanId = currentPlanId || sellingPlans[0]?.id;
		if (initialPlanId) {
			selectPlan(initialPlanId);
		}

		// Checkbox toggle handler
		checkbox.addEventListener("change", () => {
			if (checkbox.checked) {
				enableSubscription();
			} else {
				disableSubscription();
			}
		});

		// Dropdown button click handler
		dropdownButton?.addEventListener("click", e => {
			e.stopPropagation();
			const isOpen = dropdownList?.classList.contains(
				"subscription-frequency-list--open",
			);
			if (isOpen) {
				closeDropdown();
			} else {
				openDropdown();
			}
		});

		// Option selection handlers
		const options = dropdownList?.querySelectorAll(
			".subscription-frequency-option",
		);
		options?.forEach(option => {
			option.addEventListener("click", e => {
				e.stopPropagation();
				const optionElement = /** @type {HTMLElement} */ (option);
				const planId = optionElement.dataset.sellingPlanId;
				if (planId) {
					selectPlan(planId);
				}
				closeDropdown();
			});
		});

		// Close dropdown on outside click
		document.addEventListener("click", e => {
			if (!subscriptionContainer.contains(/** @type {Node} */ (e.target))) {
				closeDropdown();
			}
		});

		// Close dropdown on Escape key
		document.addEventListener("keydown", e => {
			if (e.key === "Escape") {
				closeDropdown();
			}
		});
	}
}

if (!customElements.get("quick-add-component")) {
	customElements.define("quick-add-component", QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
	#abortController = new AbortController();

	connectedCallback() {
		super.connectedCallback();

		this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
			signal: this.#abortController.signal,
		});
		this.addEventListener(
			ThemeEvents.variantUpdate,
			this.#updateProductTitleLink,
		);
		this.addEventListener(
			ThemeEvents.variantUpdate,
			this.#shortenAddToCartText,
		);

		this.addEventListener(
			DialogCloseEvent.eventName,
			this.#handleDialogClose,
		);
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		this.#abortController.abort();
		this.removeEventListener(
			DialogCloseEvent.eventName,
			this.#handleDialogClose,
		);
	}

	/**
	 * Closes the dialog
	 * @param {CartUpdateEvent} event - The cart update event
	 */
	handleCartUpdate = event => {
		if (event.detail.data.didError) return;
		this.closeDialog();
	};

	#updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
		const anchorElement = /** @type {HTMLAnchorElement} */ (
			event.detail.data.html?.querySelector(".view-product-title a")
		);
		const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
			this.querySelector(".view-product-title a")
		);
		const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
			this.querySelector(".product-header a")
		);

		if (!anchorElement) return;

		if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
		if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
	};

	#shortenAddToCartText = () => {
		const modalAddBtn = this.querySelector(".add-to-cart-text__content");
		const modalAddBtnContent = modalAddBtn?.textContent?.toLowerCase();
		if (modalAddBtn && modalAddBtnContent?.includes("add")) {
			modalAddBtn.textContent = "Add";
		}
	};

	#handleDialogClose = () => {
		const iosVersion = getIOSVersion();
		/**
		 * This is a patch to solve an issue with the UI freezing when the dialog is closed.
		 * To reproduce it, use iOS 16.0.
		 */
		if (
			!iosVersion ||
			iosVersion.major >= 17 ||
			(iosVersion.major === 16 && iosVersion.minor >= 4)
		)
			return;

		requestAnimationFrame(() => {
			/** @type {HTMLElement | null} */
			const grid = document.querySelector(
				"#ResultsList [product-grid-view]",
			);
			if (grid) {
				const currentWidth = grid.getBoundingClientRect().width;
				grid.style.width = `${currentWidth - 1}px`;
				requestAnimationFrame(() => {
					grid.style.width = "";
				});
			}
		});
	};
}

if (!customElements.get("quick-add-dialog")) {
	customElements.define("quick-add-dialog", QuickAddDialog);
}
